import { useCallback, useEffect, useState } from 'react';
import {
  IBudgetListItem,
  IBudgetsListResponse,
  fetchBudgetUserProfile,
} from '../../../../../api/fetchBudget';
import { BudgetStatusEnum, getBudgetStatusFilter } from '../../../../../enums/budgetStatus';
import { BudgetRecipientEnum, IBudgetFormData } from '../../../createAndEdit/types';

interface IUseConsultantListJourneyParams {
  budgetsListResponse: IBudgetsListResponse | null | undefined;
  userProfile: BudgetRecipientEnum | undefined;
  onNavigateToView: (budgetId: string, data: IBudgetFormData) => void;
  onNavigateToEdit: (budgetId: string, data: IBudgetFormData) => void;
  loadBudgetData: (budgetId: string, versionNumber?: number) => Promise<IBudgetFormData | null>;
}

export const useConsultantListJourney = (params: IUseConsultantListJourneyParams) => {
  const {
    budgetsListResponse,
    userProfile: userProfileProp,
    onNavigateToView,
    onNavigateToEdit,
    loadBudgetData,
  } = params;

  const [userProfile, setUserProfile] = useState<BudgetRecipientEnum | undefined>(userProfileProp);

  useEffect(() => {
    if (!userProfileProp) {
      const loadProfile = async () => {
        const profile = await fetchBudgetUserProfile();
        setUserProfile(profile);
      };
      loadProfile();
    } else {
      setUserProfile(userProfileProp);
    }
  }, [userProfileProp]);

  const handleViewOrEditBudget = useCallback(
    async (budgetId: string) => {
      let currentUserProfile = userProfile;

      if (!currentUserProfile) {
        currentUserProfile = await fetchBudgetUserProfile();
        if (currentUserProfile) {
          setUserProfile(currentUserProfile);
        }
      }

      const budgetItem = budgetsListResponse?.budgets.find(
        (item: IBudgetListItem) => item.uuid === budgetId,
      );

      const budgetStatus = budgetItem?.status as BudgetStatusEnum | string;
      const budgetRecipient = budgetItem?.recipient as BudgetRecipientEnum | undefined;

      if (!currentUserProfile || !budgetRecipient) {
        const budgetData = await loadBudgetData(budgetId);
        if (budgetData) {
          onNavigateToView(budgetId, budgetData);
        }
        return;
      }

      const statusEnum =
        typeof budgetStatus === 'string'
          ? getBudgetStatusFilter(budgetStatus)
          : (budgetStatus as BudgetStatusEnum);

      if (
        statusEnum === BudgetStatusEnum.PENDING ||
        statusEnum === BudgetStatusEnum.APPROVED ||
        budgetStatus === 'Pendente' ||
        budgetStatus === 'Aprovado'
      ) {
        const budgetData = await loadBudgetData(budgetId);
        if (budgetData) {
          onNavigateToView(budgetId, budgetData);
        }
        return;
      }

      const isRejectedBudgetWithManagerRecipient =
        statusEnum === BudgetStatusEnum.REJECTED &&
        budgetRecipient === BudgetRecipientEnum.MANAGER &&
        currentUserProfile === BudgetRecipientEnum.CONSULTANT;

      if (isRejectedBudgetWithManagerRecipient) {
        const budgetItem = budgetsListResponse?.budgets.find(
          (item: IBudgetListItem) => item.uuid === budgetId,
        );
        const versionNumber = budgetItem?.lastVersionNumber || 1;

        const editData = await loadBudgetData(budgetId, versionNumber);

        if (editData) {
          editData.versionNumber = versionNumber;
          if (budgetItem) {
            editData.status = budgetItem.status as BudgetStatusEnum;
            if (budgetItem.recipient) {
              editData.recipient = budgetItem.recipient;
            }
          }
          onNavigateToEdit(budgetId, editData);
        }
        return;
      }

      const isRecipientDifferentFromUser = budgetRecipient !== currentUserProfile;

      if (isRecipientDifferentFromUser) {
        const budgetData = await loadBudgetData(budgetId);
        if (budgetData) {
          onNavigateToView(budgetId, budgetData);
        }
      } else {
        const budgetItem = budgetsListResponse?.budgets.find(
          (item: IBudgetListItem) => item.uuid === budgetId,
        );
        const versionNumber = budgetItem?.lastVersionNumber || 1;
        const editData = await loadBudgetData(budgetId, versionNumber);
        if (editData) {
          editData.versionNumber = versionNumber;
          if (budgetItem) {
            editData.status = budgetItem.status as BudgetStatusEnum;
            if (budgetItem.recipient) {
              editData.recipient = budgetItem.recipient;
            }
          }
          onNavigateToEdit(budgetId, editData);
        }
      }
    },
    [budgetsListResponse, userProfile, onNavigateToView, onNavigateToEdit, loadBudgetData],
  );

  return {
    handleViewOrEditBudget,
  };
};
